import { Drawer, useTableRowSelection } from '@/components';
import type { PaymentContractFormSchema } from '@/features/project/projects/_models/payment-contract';
import { useContractorPayments } from '@/features/project/projects/detail/payment/_hooks/payment';
import {
  useCreatePaymentContract,
  useUpdatePaymentContract,
} from '@/features/project/projects/detail/payment/_hooks/payment-contract';
import { usePaymentGroups } from '@/features/project/projects/detail/payment/_hooks/payment-group';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';
import { PaymentContractForm } from './PaymentContractForm';

type FormValues = z.infer<typeof PaymentContractFormSchema>;

export const PaymentContractFormDrawer: FC = () => {
  const { t } = useTranslation();
  const { drawerState, setDrawerState } = useProjectDetailDrawerState();
  const { clearAllSelections } = useTableRowSelection();
  const { openMessage } = useMessage();

  const { id: projectId } = useId();
  const paymentGroups = usePaymentGroups({ projectId });
  const contractorPayments = useContractorPayments({ projectId });

  const open = drawerState.open && drawerState.type === 'payment-contract';
  const isEdit = open && drawerState.isEditing;

  const { mutateAsync: createPaymentContractAsync } =
    useCreatePaymentContract();
  const { mutateAsync: updatePaymentContractAsync } =
    useUpdatePaymentContract();

  const handleClose = async () => {
    setDrawerState({ open: false });
    clearAllSelections();
  };

  const initialValues = useMemo<Partial<FormValues> | undefined>(() => {
    if (!open) {
      return undefined;
    }

    if (!isEdit) {
      return {
        paymentId: drawerState.paymentId,
        amountBreakdown: [],
        hideBidRate: false,
      };
    }

    const contract = [
      ...(paymentGroups.data?.find((pg) => pg.id === drawerState.paymentGroupId)
        ?.payments ?? []),
      ...(contractorPayments.data ?? []),
    ]
      .find((payment) => payment.id === drawerState.paymentId)
      ?.contracts.find(
        (contract) => contract.id === drawerState.paymentContractId,
      );

    return contract
      ? {
          ...contract,
          contractMethod: contract.contractMethod ?? undefined,
          bidRate: contract.bidRate ?? undefined,
        }
      : undefined;
  }, [drawerState, isEdit, open, paymentGroups.data, contractorPayments.data]);

  return (
    <Drawer open={open} onClose={handleClose} destroyOnClose>
      <PaymentContractForm
        projectId={projectId}
        paymentType={open ? drawerState.paymentType : 'payee'}
        initialValues={initialValues}
        onSubmit={async ({ values, nextDrawerKey, onSuccess }) => {
          invariant(open, t('unreachable-code'));
          if (drawerState.isEditing) {
            await updatePaymentContractAsync({
              id: drawerState.paymentContractId,
              paymentGroupId: drawerState.paymentGroupId,
              projectId,
              ...values,
            });

            await Promise.all([
              openMessage('success', t('message-update-completed')),
              (async () => {
                setDrawerState({ open: false });
              })(),
            ]);
          } else {
            await createPaymentContractAsync({
              paymentGroupId: drawerState.paymentGroupId,
              projectId,
              ...values,
            });

            await Promise.all([
              openMessage('success', t('add-completed')),
              (async () => {
                if (!nextDrawerKey) {
                  setDrawerState({ open: false });
                }
              })(),
              (async () => {
                onSuccess?.();
              })(),
            ]);
          }
        }}
      />
    </Drawer>
  );
};
